/**
 * Says, quietly, whether the app is ready to work with no signal.
 *
 * This is the product's central claim, and a claim a diver cannot verify until
 * they are somewhere with no bars — which is exactly the wrong moment to find
 * out. So the app tells them on shore.
 */

import { useEffect, useState } from "react";

export function OfflineBadge() {
  // Guarded because this also has to survive a prerender, where there is no
  // navigator at all. Assume online: the badge corrects itself on mount.
  const [online, setOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);

    if ("serviceWorker" in navigator) {
      if (navigator.serviceWorker.controller) setReady(true);
      const claimed = () => setReady(true);
      navigator.serviceWorker.addEventListener("controllerchange", claimed);
      return () => {
        window.removeEventListener("online", on);
        window.removeEventListener("offline", off);
        navigator.serviceWorker.removeEventListener("controllerchange", claimed);
      };
    }

    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const [label, tone] = !online
    ? ["Offline — everything still works", "border-deep bg-deep/10"]
    : ready
      ? ["Saved for offline", "border-graphite-2/40 bg-graphite/5"]
      : ["Caching for offline…", "border-graphite-2/40 bg-graphite/5"];

  return (
    <span
      className={`no-print inline-flex items-center gap-1.5 rounded-[2px] border px-2 py-0.5 font-display text-[12px] font-semibold uppercase tracking-[0.08em] text-graphite-2 ${tone}`}
    >
      <span
        aria-hidden
        className={`inline-block h-1.5 w-1.5 rounded-full ${ready || !online ? "bg-deep" : "bg-graphite-2/50"}`}
      />
      {label}
    </span>
  );
}
